import { useCallback, useEffect, useMemo, useState } from 'react';
import { ThemeContext, THEME_STORAGE_KEY } from './context.js';

const DARK_QUERY = '(prefers-color-scheme: dark)';

function initialTheme() {
  // index.html has already resolved and applied this; read it back so the two agree.
  const applied = document.documentElement.getAttribute('data-bs-theme');
  if (applied === 'light' || applied === 'dark') return applied;

  return window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light';
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  // Follow the OS until the user makes an explicit choice.
  useEffect(() => {
    if (localStorage.getItem(THEME_STORAGE_KEY)) return undefined;

    const media = window.matchMedia?.(DARK_QUERY);
    if (!media) return undefined;

    const onChange = (event) => setTheme(event.matches ? 'dark' : 'light');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((current) => {
      const next = current === 'dark' ? 'light' : 'dark';
      localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, isDark: theme === 'dark', toggleTheme }),
    [theme, toggleTheme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
